import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../api';
import { can } from '../auth';

// What the office did to a parcel, under the Tracking History on its page:
// prices, weights, billing, drivers, addresses, parties, notes, boxes, extras,
// proofs, and the messages sent to the client. Scans are not here — they are
// the Tracking History itself. The API strips money for users without
// billing.view, so a sentence without an amount is the expected view for them.

const money = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? null : `R${Number(v).toFixed(2)}`);
const kg = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? null : `${Number(v)} kg`);
const words = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '?');
const arrow = (from, to, empty = 'none') => (from == null || from === to ? to : `${from ?? empty} → ${to ?? empty}`);

const GROUPS = {
  billing: ['parcel_measured', 'parcel_repriced', 'parcel_weight_updated', 'parcel_invoiced',
    'parcel_released_from_invoice', 'parcel_invoice_issued', 'parcel_bill_to_changed',
    'parcel_extra_added', 'parcel_extra_removed', 'driver_pay_manual_set'],
  driver:  ['parcel_assigned', 'parcel_pickup_assigned', 'parcels_bulk_assigned', 'parcel_scan', 'scan_driver_corrected'],
  parties: ['parcel_address_changed', 'parcel_parties_changed'],
};
const groupOf = (row) => {
  if (row.kind === 'message' || row.action === 'proof_sent') return 'messages';
  for (const [g, list] of Object.entries(GROUPS)) if (list.includes(row.action)) return g;
  return 'other';
};
const FILTERS = [
  ['all', 'All'], ['billing', 'Price & billing'], ['driver', 'Drivers'],
  ['parties', 'Address & parties'], ['messages', 'Messages'], ['other', 'Other'],
];

const COLORS = {
  billing: 'bg-emerald-100 text-emerald-700',
  driver: 'bg-orange-100 text-orange-700',
  parties: 'bg-sky-100 text-sky-700',
  messages: 'bg-violet-100 text-violet-700',
  other: 'bg-gray-100 text-gray-700',
  danger: 'bg-red-100 text-red-700',
};

// One short label and one plain sentence per action. `d` is the details object.
function describe(row) {
  const d = row.details || {};
  const b = d.before || {};
  switch (row.action) {
    case 'parcel_created':
      return ['Booked', [d.description, d.weight ? kg(d.weight) : null, money(d.manual_price) ? `manual price ${money(d.manual_price)}` : null,
        d.boxes > 1 ? `${d.boxes} boxes` : null].filter(Boolean).join(' · ') || 'Parcel booked'];
    case 'parcel_measured': {
      const src = d.price_source === 'manual' ? 'manual' : String(d.price_source || '').startsWith('over:') ? d.price_source.slice(5) : d.price_source ? `tier ${d.price_source}` : null;
      const parts = [];
      if (money(d.price)) parts.push(`Price ${arrow(money(b.price), money(d.price))}${src ? ` (${src})` : ''}`);
      else parts.push('Priced' + (src ? ` (${src})` : ''));
      if (d.chargeable_kg != null) parts.push(`chargeable ${arrow(kg(b.chargeable_kg), kg(d.chargeable_kg))}`);
      else if (d.weight) parts.push(`weight ${arrow(kg(b.weight), kg(d.weight))}`);
      if (d.measured === 'per box') parts.push('measured per box');
      return [b.price != null ? 'Price changed' : 'Priced', parts.join(' · ')];
    }
    case 'parcel_repriced':
      return ['Re-priced on invoice', `${money(d.price_to) ? `Price ${arrow(money(d.price_from), money(d.price_to))}` : 'Price changed'}${d.tier ? ` (${d.tier})` : ''} · while invoicing${d.invoice && d.invoice !== 'draft' ? ` ${d.invoice}` : ''}`];
    case 'parcel_weight_updated': {
      const parts = [];
      if (d.weight != null) parts.push(`Weight ${arrow(kg(b.weight), kg(d.weight))}`);
      if (d.length && d.width && d.height) parts.push(`${d.length}×${d.width}×${d.height} cm`);
      if (d.chargeable_kg != null) parts.push(`chargeable ${arrow(kg(b.chargeable_kg), kg(d.chargeable_kg))}`);
      return ['Weight', parts.join(' · ') + ' · price not changed'];
    }
    case 'parcel_invoiced':
      return ['Invoiced', `Put on ${d.invoice === 'draft' || !d.invoice ? 'a draft invoice' : `invoice ${d.invoice}`}${d.client ? ` for ${d.client}` : ''}${money(d.line_discount_amount) ? ` · discount ${money(d.line_discount_amount)}` : ''}`];
    case 'parcel_invoice_issued':
      return ['Invoice issued', `Invoice ${d.invoice} issued${d.client ? ` to ${d.client}` : ''}`];
    case 'parcel_released_from_invoice': {
      const why = { draft_edited: 'taken off the draft', draft_deleted: 'the draft was deleted', invoice_edited: 'taken off the invoice',
        credit_note: `credited${d.credit_note ? ` (${d.credit_note})` : ''}`, invoice_cancelled: 'the invoice was cancelled' }[d.reason] || words(d.reason);
      return ['Off invoice', `Back to unbilled — ${why}${d.invoice && d.invoice !== 'draft' ? ` · ${d.invoice}` : ''}`];
    }
    case 'parcel_bill_to_changed':
      return ['Payer changed', d.bill_to_to ? `Billed to ${arrow(d.bill_to_from, d.bill_to_to)}` : 'Payer changed'];
    case 'parcel_parties_changed': {
      const parts = [];
      if (d.sender_to) parts.push(`Sender ${arrow(d.sender_from, d.sender_to)}`);
      if (d.recipient_to) parts.push(`Recipient ${arrow(d.recipient_from, d.recipient_to)}`);
      if (d.bill_to) parts.push(`payer ${d.bill_to_from && d.bill_to_from !== d.bill_to ? arrow(d.bill_to_from, d.bill_to) : d.bill_to}`);
      if (d.reason === 'client_merge') parts.push('clients merged');
      return ['Client changed', parts.join(' · ')];
    }
    case 'parcel_address_changed': {
      const from = d.before?.address, to = d.after?.address;
      return [`${d.leg === 'pickup' ? 'Pickup' : 'Delivery'} address`,
        `${from && from !== to ? `${from} → ` : ''}${to || 'pin only'}${d.after?.lat ? ' · pinned' : ''}${d.update_client ? ` · ${d.client || 'client'} record updated too` : ' · this parcel only'}`];
    }
    case 'parcel_notes_updated':
      return ['Notes', d.notes_to !== undefined ? arrow(d.notes_from || '(empty)', d.notes_to || '(cleared)', '(empty)') : (d.notes || '(cleared)')];
    case 'parcel_assigned':
      return ['Delivery driver', `${arrow(d.driver_from, d.driver_name || 'unassigned', 'none')}${d.scheduled_delivery ? ` · delivery ${d.scheduled_delivery_from && d.scheduled_delivery_from !== d.scheduled_delivery ? `${d.scheduled_delivery_from} → ` : ''}${d.scheduled_delivery}` : ''}`];
    case 'parcel_pickup_assigned':
      return ['Pickup driver', arrow(d.driver_from, d.driver_name || 'unassigned', 'none')];
    case 'parcels_bulk_assigned':
      return [`${d.type === 'pickup' ? 'Pickup' : 'Delivery'} driver`, `${d.driver_name || 'unassigned'} · batch of ${d.count}`];
    case 'driver_pay_manual_set':
      return ['Driver pay',
        'amount_to' in d
          ? (d.amount_to == null ? `Manual driver pay removed — back to the weight table${money(d.amount_from) ? ` (was ${money(d.amount_from)})` : ''}`
                                  : `Driver paid a manual ${arrow(money(d.amount_from), money(d.amount_to))} for this parcel${d.note ? ` · ${d.note}` : ''}`)
          : `Manual driver pay ${d.amount_to === null ? 'removed' : 'set'}${d.note ? ` · ${d.note}` : ''}`];
    case 'scan_dated':
      return ['Scan re-dated', `${words(d.event_type)} moved from ${when(d.scanned_at_from)} to ${when(d.scanned_at_to)}${d.booking_to ? ` · booking date moved to ${when(d.booking_to)}` : ''}`];
    case 'scan_driver_corrected':
      return ['Scan re-credited', `${words(d.event_type)} scan credited to ${d.driver_to || 'unknown'} instead of ${d.driver_from || 'no driver'}${d.assigned_driver_updated ? ' · delivery driver updated too' : ''}`];
    case 'parcel_scan':
      return ['Driver took it', `${row.user_name || 'Driver'} became the delivery driver by scanning ${words(d.event_type).toLowerCase()}`];
    case 'parcel_cancelled':
      return ['Cancelled', d.stickers_released?.length ? `Stickers released: ${d.stickers_released.join(', ')}` : 'Parcel cancelled'];
    case 'parcel_boxes_added':
      return ['Boxes added', `${d.added?.length || 0} box${d.added?.length === 1 ? '' : 'es'} added (${(d.added || []).join(', ')}) · now ${d.total_boxes}`];
    case 'parcel_box_removed':
      return ['Box removed', `Box ${d.box} (${d.barcode}) removed`];
    case 'box_status_changed':
      return ['Box status', `Box ${d.box}: ${words(d.from)} → ${words(d.to)}`];
    case 'parcel_extra_added':
      return ['Extra item', `${d.description} × ${d.quantity}${money(d.unit_price) ? ` at ${money(d.unit_price)}` : ''} added`];
    case 'parcel_extra_removed':
      return ['Extra item', `${d.description || 'Item'} removed`];
    case 'proof_recorded':
      return ['Proof', `${words(d.stage)} proof: ${words(d.proof_type)}${d.signed_by ? ` signed by ${d.signed_by}` : ''}${d.note ? ` · ${d.note}` : ''}`];
    case 'proof_sent':
      return ['Proof sent', `${words(d.stage)} proof sent by ${d.channel} to ${d.party || ''} ${d.to || ''}`.replace(/\s+/g, ' ').trim()];
    default:
      if (row.kind === 'message') {
        const status = d.status || 'sent';
        return [status === 'sent' ? 'Message sent' : status === 'failed' ? 'Message failed' : `Message ${status}`,
          `${words(d.channel)} to ${d.recipient}: ${words(d.event_type)}${d.detail ? ` · ${d.detail}` : ''}`];
      }
      return [words(row.action.replace(/^parcel_/, '')),
        Object.entries(d).filter(([k, v]) => k !== 'via' && v != null && typeof v !== 'object').map(([k, v]) => `${k}: ${v}`).join(' · ')];
  }
}

export default function ParcelChanges({ tracking }) {
  const [filter, setFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const { data: rows = [], isLoading, isError } = useQuery({
    queryKey: ['parcel-changes', tracking],
    queryFn: () => api.get(`/parcels/${tracking}/changes`).then((r) => r.data),
    staleTime: 0,
  });

  const visible = rows.filter((r) => filter === 'all' || groupOf(r) === filter);
  const shown = showAll ? visible : visible.slice(0, 8);
  const counts = rows.reduce((acc, r) => { const g = groupOf(r); acc[g] = (acc[g] || 0) + 1; return acc; }, {});

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="font-semibold text-gray-700 flex items-center gap-2"><History size={16} className="text-gray-400" /> Changes</h3>
          <p className="text-xs text-gray-400">Who did what to this parcel — prices, weights, billing, drivers, addresses, messages. Scans are in the Tracking History above.</p>
        </div>
        {can('system.audit') && (
          <a href={`/activity-log?search=${encodeURIComponent(tracking)}`} className="text-xs text-orange-500 hover:underline whitespace-nowrap">Full activity log</a>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-3 mb-3">
        {FILTERS.map(([key, label]) => {
          const n = key === 'all' ? rows.length : (counts[key] || 0);
          if (key !== 'all' && !n) return null;
          return (
            <button key={key} type="button" onClick={() => { setFilter(key); setShowAll(false); }}
              className={`text-xs px-2.5 py-1 rounded-full border ${filter === key ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
              {label} <span className={filter === key ? 'text-gray-300' : 'text-gray-400'}>{n}</span>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-red-500">Could not load the changes.</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-gray-400">{rows.length ? 'Nothing in this group.' : 'No changes recorded yet.'}</p>
      ) : (
        <ol className="divide-y divide-gray-100">
          {shown.map((r) => {
            const g = groupOf(r);
            const [label, text] = describe(r);
            const danger = ['parcel_cancelled', 'parcel_released_from_invoice', 'parcel_box_removed', 'parcel_extra_removed'].includes(r.action)
              || (r.kind === 'message' && r.details?.status === 'failed');
            return (
              <li key={r.id} className="py-2 flex items-start gap-3">
                <span className={`mt-0.5 text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${danger ? COLORS.danger : COLORS[g]}`}>{label}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 break-words">{text}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {when(r.at)}
                    {r.user_name ? <> · by <span className="text-gray-600 font-medium">{r.user_name}</span></> : r.kind === 'message' ? ' · automatic' : null}
                    {r.via && <span className={`ml-2 text-[10px] uppercase tracking-wide px-1.5 py-px rounded ${r.via === 'app' ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-100 text-gray-500'}`}>{r.via === 'app' ? 'driver app' : 'web'}</span>}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {visible.length > 8 && (
        <button type="button" onClick={() => setShowAll((v) => !v)}
          className="mt-2 text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1">
          {showAll ? <><ChevronUp size={13} /> Show fewer</> : <><ChevronDown size={13} /> Show all {visible.length}</>}
        </button>
      )}
    </div>
  );
}
